import { cn } from "@/utils/cn"
import { CheckCircle, XCircle, Clock, Loader2 } from "lucide-react"

interface StatusBadgeProps {
  status: 'approved' | 'rejected' | 'pending' | 'processing' | 'blocked'
  size?: 'sm' | 'md' | 'lg'
  showIcon?: boolean
  className?: string
}

export const StatusBadge = ({ 
  status, 
  size = 'md', 
  showIcon = true,
  className 
}: StatusBadgeProps) => {
  const statusConfig = {
    approved: { 
      color: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800', 
      icon: CheckCircle, 
      label: 'Approved' 
    },
    rejected: { 
      color: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800', 
      icon: XCircle, 
      label: 'Rejected' 
    },
    blocked: { 
      color: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800', 
      icon: XCircle, 
      label: 'Blocked' 
    },
    pending: { 
      color: 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800', 
      icon: Clock, 
      label: 'Pending' 
    },
    processing: { 
      color: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800', 
      icon: Loader2, 
      label: 'Processing' 
    }
  }
  
  const config = statusConfig[status]
  const IconComponent = config.icon
  
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm', 
    lg: 'px-4 py-2 text-base'
  }
  
  const iconSizeClasses = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5'
  }
  
  return (
    <span 
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full font-medium border transition-all duration-200",
        sizeClasses[size],
        config.color,
        status === 'processing' && 'animate-pulse',
        className
      )}
      role="status"
      aria-label={`Status: ${config.label}`}
    >
      {showIcon && (
        <IconComponent 
          className={cn(
            iconSizeClasses[size],
            status === 'processing' && 'animate-spin'
          )} 
        />
      )}
      <span>{config.label}</span>
    </span>
  )
}